import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { ErrorBoundary } from '@components/ErrorBoundary';
import ErrorView, { ErrorProps } from '@components/ErrorView';
import { KeyboardAvoidingView, LoadingView, SafeAreaView } from '@components/View';
import { useAppTheme } from '@context/ThemeContext';

const DEFAULT_SCREEN_EDGE_OFFSET = 20;
const DEFAULT_KEYBOARD_SCROLL_BOTTOM_OFFSET = DEFAULT_SCREEN_EDGE_OFFSET;
const DEFAULT_SCROLL_CONTENT_BOTTOM_PADDING = DEFAULT_SCREEN_EDGE_OFFSET;

type SafeAreaEdges = React.ComponentProps<typeof SafeAreaView>['edges'];

type ScreenContainerSafeAreaContextValue = {
  safeArea?: boolean;
  safeAreaEdges?: SafeAreaEdges;
};

const ScreenContainerSafeAreaContext = React.createContext<ScreenContainerSafeAreaContextValue>({});

export type ScreenContainerSafeAreaProviderProps = React.PropsWithChildren<{
  /** Enables safe-area wrapping for descendant screen containers by default. */
  safeArea?: boolean;
  /** Restricts which safe-area edges descendant screen containers apply by default. */
  safeAreaEdges?: SafeAreaEdges;
}>;

export function ScreenContainerSafeAreaProvider({
  children,
  safeArea = true,
  safeAreaEdges,
}: ScreenContainerSafeAreaProviderProps) {
  const value = React.useMemo(
    () => ({
      safeArea,
      safeAreaEdges,
    }),
    [safeArea, safeAreaEdges]
  );

  return (
    <ScreenContainerSafeAreaContext.Provider value={value}>
      {children}
    </ScreenContainerSafeAreaContext.Provider>
  );
}

export type ScreenContainerProps = React.ComponentProps<typeof View> & {
  loading?: boolean;
  scrollable?: boolean;
  error?: boolean;
  errorProps?: ErrorProps;
  /** Extra distance to keep between a focused input and the keyboard on scrollable screens. */
  keyboardBottomOffset?: number;
};

export default function ScreenContainer({
  loading,
  scrollable,
  children,
  error,
  errorProps,
  keyboardBottomOffset = DEFAULT_KEYBOARD_SCROLL_BOTTOM_OFFSET,
  style,
  testID,
  ...props
}: ScreenContainerProps) {
  const safeAreaDefaults = React.useContext(ScreenContainerSafeAreaContext);
  const appTheme = useAppTheme();
  const baseTestId = testID || 'components.ScreenContainer';
  const isLoading = Boolean(loading && !error);
  const content = error ? (
    <ErrorView testID={baseTestId + '.ErrorView'} {...errorProps} />
  ) : (
    <>{children}</>
  );

  const body = (
    <LoadingView
      loading={isLoading}
      indicatorSize="large"
      style={[styles.root, isLoading ? styles.loadingRoot : undefined]}
      testID={baseTestId + '.LoadingView'}
      {...props}>
      <View style={[styles.frame, { maxWidth: appTheme.maxWidth }]}>
        {renderContent(content, Boolean(scrollable), style, keyboardBottomOffset)}
      </View>
    </LoadingView>
  );

  return (
    <ErrorBoundary>
      {safeAreaDefaults.safeArea ? (
        <SafeAreaView
          edges={safeAreaDefaults.safeAreaEdges}
          style={styles.root}
          testID={`${baseTestId}.SafeAreaView`}>
          {body}
        </SafeAreaView>
      ) : (
        body
      )}
    </ErrorBoundary>
  );
}

function renderContent(
  content: React.ReactNode,
  scrollable: boolean,
  style: StyleProp<ViewStyle>,
  keyboardBottomOffset: number
) {
  if (scrollable) {
    return (
      <KeyboardAwareScrollView
        bottomOffset={keyboardBottomOffset}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={styles.scroller}
        contentContainerStyle={[styles.scrollContent, style]}>
        {content}
      </KeyboardAwareScrollView>
    );
  }

  return <KeyboardAvoidingView style={[styles.content, style]}>{content}</KeyboardAvoidingView>;
}

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  frame: {
    alignSelf: 'center',
    flex: 1,
    width: '100%',
  },
  loadingRoot: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  root: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    alignItems: 'stretch',
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: DEFAULT_SCROLL_CONTENT_BOTTOM_PADDING,
    width: '100%',
  },
  scroller: {
    flex: 1,
    width: '100%',
  },
});
